// Chat Offline Resources
//
// Provides sync, cache read, and outbox flush utilities for the WIN Chat PWA.
// All functions import from chat_db.js (single Dexie instance).
//
// Usage:
//   import { syncChats, getCachedChats, getCachedMessages, flushOutbox } from "./chat_offline_resources"

import db from "./chat_db"

const LAST_SYNC_KEY = "chat:last-sync"

// ── Sync ──────────────────────────────────────────────────────────────────

// Fetches /chat/sync.json and writes results to IndexedDB.
// onProgress(count) is called with the number of chats synced.
// Returns { chats, messages } on success.
export async function syncChats(onProgress) {
  const response = await fetch("/chat/sync.json", {
    credentials: "include",
    headers: { "Accept": "application/json" }
  })

  if (!response.ok) {
    throw new Error(`Sync failed: ${response.status} ${response.statusText}`)
  }

  const data = await response.json()
  const { chats, messages } = data

  // Write chats to IDB
  await db.cached_chats.bulkPut(
    chats.map(chat => ({
      id:              chat.id,
      name:            chat.name,
      display_name:    chat.display_name,
      chat_type:       chat.chat_type,
      last_message_at: chat.last_message_at,
      unread_count:    chat.unread_count,
      participants:    chat.participants
    }))
  )

  // Write messages to IDB, keyed per chat
  const allMessages = []
  Object.entries(messages).forEach(([chatId, chatMessages]) => {
    chatMessages.forEach(msg => {
      allMessages.push({
        id:          msg.id,
        chat_id:     parseInt(chatId),
        sender_id:   msg.sender_id,
        sender_name: msg.sender_name,
        content:     msg.content,
        created_at:  msg.created_at
      })
    })
  })

  if (allMessages.length > 0) {
    await db.cached_messages.bulkPut(allMessages)
  }

  // Write participants to IDB
  const allParticipants = []
  chats.forEach(chat => {
    chat.participants.forEach(cp => {
      allParticipants.push({
        id:      cp.id,
        chat_id: chat.id,
        user_id: cp.user_id,
        name:    cp.name,
        role:    cp.role
      })
    })
  })

  if (allParticipants.length > 0) {
    await db.cached_participants.bulkPut(allParticipants)
  }

  markSynced()
  if (onProgress) onProgress(chats.length)

  return { chats, messages }
}

// ── Read cache ────────────────────────────────────────────────────────────

// Returns all cached chats sorted by last_message_at descending.
export async function getCachedChats() {
  const chats = await db.cached_chats.toArray()
  return chats.sort((a, b) => {
    const dateA = new Date(a.last_message_at || 0)
    const dateB = new Date(b.last_message_at || 0)
    return dateB - dateA
  })
}

// Returns cached messages for a given chat_id, sorted by created_at ascending.
export async function getCachedMessages(chatId) {
  const messages = await db.cached_messages
    .where("chat_id")
    .equals(chatId)
    .sortBy("created_at")
  return messages
}

// Returns all pending outbox messages for a given chat_id.
export async function getOutboxMessages(chatId) {
  if (chatId !== undefined) {
    return db.outbox.where("chat_id").equals(chatId).sortBy("created_at")
  }
  return db.outbox.orderBy("created_at").toArray()
}

// Adds a message to the outbox for sending when back online.
export async function queueOutboxMessage(chatId, content) {
  return db.outbox.add({
    chat_id:     chatId,
    content:     content,
    created_at:  new Date().toISOString(),
    retry_count: 0,
    status:      "pending"
  })
}

// ── Outbox flush ──────────────────────────────────────────────────────────

// Attempts to send all pending outbox messages.
// On success: deletes from outbox.
// On failure: increments retry_count. Abandons after 3 failures.
// Returns { sent, failed } counts.
export async function flushOutbox(chatId) {
  const pending = await getOutboxMessages(chatId)
  if (pending.length === 0) return { sent: 0, failed: 0 }

  const csrf = document.querySelector("meta[name=csrf-token]")?.content || ""
  let sent   = 0
  let failed = 0

  for (const msg of pending) {
    // Skip messages that have failed too many times
    if ((msg.retry_count || 0) >= 3) {
      failed++
      continue
    }

    try {
      const body = new URLSearchParams()
      body.append("chat_message[content]", msg.content)
      body.append("authenticity_token", csrf)

      const res = await fetch(`/chats/${msg.chat_id}/chat_messages`, {
        method:      "POST",
        credentials: "include",
        headers:     {
          "Content-Type": "application/x-www-form-urlencoded",
          "Accept":       "text/vnd.turbo-stream.html, text/html"
        },
        body: body.toString()
      })

      if (res.ok) {
        await db.outbox.delete(msg.id)
        sent++
      } else {
        await db.outbox.update(msg.id, { retry_count: (msg.retry_count || 0) + 1, status: "failed" })
        failed++
      }
    } catch {
      await db.outbox.update(msg.id, { retry_count: (msg.retry_count || 0) + 1, status: "failed" })
      failed++
    }
  }

  return { sent, failed }
}

// ── Sync timestamp ────────────────────────────────────────────────────────

export function lastSyncedAt() {
  return localStorage.getItem(LAST_SYNC_KEY)
}

export function markSynced() {
  localStorage.setItem(LAST_SYNC_KEY, new Date().toISOString())
}
